import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useTeam } from './teamContext';
import axios from 'axios';

const SubscriptionContext = createContext();

// Premium plans that should give users access to all features
const PREMIUM_PLANS = ['starter', 'pro', 'enterprise'];
// Non-premium plans (these should NOT give access to premium features)
const NON_PREMIUM_PLANS = ['free'];

export const SubscriptionProvider = ({ children }) => {
  const { selectedTeam, isLoading: teamLoading } = useTeam();
  const hasLoadedRef = useRef(false); // Track if we've loaded subscription data
  const currentTeamIdRef = useRef(null); // Track current team ID
  const isRefreshingRef = useRef(false);
  const pendingRequestRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const MAX_RETRIES = 4;
  const hasHydratedFromCacheRef = useRef(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    // Initialize with cached data if available to prevent flash
    try {
      // First try sessionStorage (persists for the current browser session)
      const sessionCache = sessionStorage.getItem('subscriptionStatus');
      if (sessionCache) {
        const cached = JSON.parse(sessionCache);
        // NOTE: cannot touch refs safely during lazy init; we'll infer cache later.
        return {
          isActive: cached.isActive || false,
          plan: cached.plan || 'free',
          status: cached.status || 'incomplete',
          loading: false,
          error: null,
          hasCQIntelligence: cached.hasCQIntelligence || false,
          limits: cached.limits || {},
          accessiblePages: cached.accessiblePages || ['dashboard', 'settings'],
          pageAccess: cached.pageAccess || {},
          billingCycle: cached.billingCycle || 'monthly',
          currentPeriodStart: cached.currentPeriodStart || null,
          currentPeriodEnd: cached.currentPeriodEnd || null,
          stripeCustomerId: cached.stripeCustomerId || null,
          stripeSubscriptionId: cached.stripeSubscriptionId || null
        };
      }
      
      // Fallback to localStorage subscription cache
      const subscriptionCache = localStorage.getItem('subscriptionStatus');
      if (subscriptionCache) {
        const cached = JSON.parse(subscriptionCache);
        // Check if cache is recent (within 5 minutes)
        const isRecent = cached.timestamp && (Date.now() - cached.timestamp) < 5 * 60 * 1000;
        if (isRecent) {
          // Store in sessionStorage for faster future access
          sessionStorage.setItem('subscriptionStatus', JSON.stringify(cached));
          // NOTE: cannot touch refs safely during lazy init; we'll infer cache later.
          return {
            isActive: cached.isActive || false,
            plan: cached.plan || 'free',
            status: cached.status || 'incomplete',
            loading: false,
            error: null,
            hasCQIntelligence: cached.hasCQIntelligence || false,
            limits: cached.limits || {},
            accessiblePages: cached.accessiblePages || ['dashboard', 'settings'],
            pageAccess: cached.pageAccess || {},
            billingCycle: cached.billingCycle || 'monthly',
            currentPeriodStart: cached.currentPeriodStart || null,
            currentPeriodEnd: cached.currentPeriodEnd || null,
            stripeCustomerId: cached.stripeCustomerId || null,
            stripeSubscriptionId: cached.stripeSubscriptionId || null
          };
        }
      }
      
      // Fallback to team data cache
      const cachedTeamData = localStorage.getItem('selectedTeamData');
      if (cachedTeamData) {
        const teamData = JSON.parse(cachedTeamData);
        if (teamData && teamData.subscription) {
          const { plan, status, hasCQIntelligence } = teamData.subscription;
          const planLower = (plan || 'free').toLowerCase();
          const isPremiumPlan = PREMIUM_PLANS.includes(planLower);
          const isActive = (status && status.toLowerCase() === 'active') && isPremiumPlan;
          
          // Store in sessionStorage
          const cacheData = {
            isActive,
            plan: plan || 'free',
            status: status || 'incomplete',
            hasCQIntelligence: hasCQIntelligence || false,
            billingCycle: teamData.subscription.billingCycle || 'monthly',
            currentPeriodStart: teamData.subscription.currentPeriodStart || null,
            currentPeriodEnd: teamData.subscription.currentPeriodEnd || null,
            stripeCustomerId: teamData.subscription.stripeCustomerId || null,
            stripeSubscriptionId: teamData.subscription.stripeSubscriptionId || null
          };
          sessionStorage.setItem('subscriptionStatus', JSON.stringify(cacheData));
          // NOTE: cannot touch refs safely during lazy init; we'll infer cache later.
          
          return {
            ...cacheData,
            loading: false, // Set to false since we have cached data
            error: null,
            limits: {},
            accessiblePages: ['dashboard', 'settings'],
            pageAccess: {},
            billingCycle: cacheData.billingCycle || 'monthly',
            currentPeriodStart: cacheData.currentPeriodStart || null,
            currentPeriodEnd: cacheData.currentPeriodEnd || null,
            stripeCustomerId: cacheData.stripeCustomerId || null,
            stripeSubscriptionId: cacheData.stripeSubscriptionId || null
          };
        }
      }
    } catch (error) {
      console.error('Error initializing subscription from cache:', error);
    }
    
    // Default state if no cached data - start with loading true to prevent flash
    // This ensures we wait for team context to load before making decisions
    return {
      isActive: false,
      plan: 'free',
      status: 'incomplete',
      loading: true, // Start with true to prevent flash of upgrade screen
      error: null,
      hasCQIntelligence: false,
      limits: {},
      accessiblePages: ['dashboard', 'settings'],
      pageAccess: {},
      billingCycle: 'monthly',
      currentPeriodStart: null,
      currentPeriodEnd: null,
      stripeCustomerId: null,
      stripeSubscriptionId: null
    };
  });

  const buildStateFromSubscription = useCallback((billingData) => {
    if (!billingData) return null;

    return {
      isActive: billingData.isActive || false,
      plan: billingData.effectivePlan || billingData.plan || 'free',
      status: billingData.status || 'incomplete',
      loading: false,
      error: null,
      hasCQIntelligence: billingData.hasCQIntelligence || false,
      limits: billingData.limits || {},
      features: billingData.features || [],
      accessiblePages: billingData.accessiblePages || [],
      pageAccess: billingData.pageAccess || {},
      billingCycle: billingData.billingCycle || 'monthly',
      currentPeriodStart: billingData.currentPeriodStart || null,
      currentPeriodEnd: billingData.currentPeriodEnd || null,
      cancelAtPeriodEnd: billingData.cancelAtPeriodEnd || false,
      stripeCustomerId: billingData.stripeCustomerId || null,
      stripeSubscriptionId: billingData.stripeSubscriptionId || null,
      // New billing-specific fields
      subscription: billingData.subscription || null,
      planConfig: billingData.planConfig || null,
      amount: billingData.subscription?.amount || 0,
      currency: billingData.subscription?.currency || 'USD',
      teamId: billingData.teamId || null
    };
  }, []);

  // Function to force refresh subscription data (clear cache and refetch)
  const refreshSubscription = useCallback(
    async (teamId, options = {}) => {
      if (!teamId) return false;

      const { force = false, skipRetry = false, silent = false } = options;
      
      // If force refresh, clear all caches
      if (force) {
        sessionStorage.removeItem('subscriptionStatus');
        localStorage.removeItem('subscriptionStatus');
        // Also clear team data cache to force fresh fetch
        const teamData = localStorage.getItem('selectedTeamData');
        if (teamData) {
          try {
            const parsed = JSON.parse(teamData);
            // Remove timestamp to force refresh
            delete parsed._lastFetched;
            localStorage.setItem('selectedTeamData', JSON.stringify(parsed));
          } catch (e) {
          }
        }
      }

      if (currentTeamIdRef.current !== teamId) {
        retryAttemptsRef.current = 0;
      }

      // If a request is already in flight, reuse it unless force is specified
      if (pendingRequestRef.current && !force) {
        return pendingRequestRef.current;
      }

      if (isRefreshingRef.current && !force) {
        return pendingRequestRef.current;
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      const API_URL = process.env.REACT_APP_API_SERVER_URL;
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

      const fetchPromise = (async () => {
        isRefreshingRef.current = true;
        // If we already have cached subscription info, refresh in the background
        // to avoid flashing "Verifying access..." on every navigation.
        setSubscriptionStatus(prev => ({
          ...prev,
          loading: silent ? prev.loading : true,
          error: null
        }));

        try {
          const response = await axios.get(`${API_URL}/api/billing/status/${teamId}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              'Content-Type': 'application/json'
            }
          });

          if (response.data && response.data.success && response.data.data) {
            const subscriptionData = response.data.data;
            const state = buildStateFromSubscription(subscriptionData);

            if (state) {
              setSubscriptionStatus(state);
              hasLoadedRef.current = true;
              currentTeamIdRef.current = teamId;
              retryAttemptsRef.current = 0;

              const { loading, error, ...cachePayload } = {
                ...state,
                timestamp: Date.now()
              };

              sessionStorage.setItem('subscriptionStatus', JSON.stringify(cachePayload));
              localStorage.setItem('subscriptionStatus', JSON.stringify(cachePayload));

              const cachedTeamData = localStorage.getItem('selectedTeamData');
              if (cachedTeamData) {
                try {
                  const parsedTeam = JSON.parse(cachedTeamData);
                  parsedTeam.subscription = {
                    plan: state.plan,
                    status: state.status,
                    hasCQIntelligence: state.hasCQIntelligence,
                    billingCycle: state.billingCycle,
                    currentPeriodStart: state.currentPeriodStart,
                    currentPeriodEnd: state.currentPeriodEnd,
                    stripeCustomerId: state.stripeCustomerId,
                    stripeSubscriptionId: state.stripeSubscriptionId
                  };
                  localStorage.setItem('selectedTeamData', JSON.stringify(parsedTeam));
                } catch (err) {
                  console.error('Error updating cached team data:', err);
                }
              }

          window.dispatchEvent(
            new CustomEvent('subscriptionUpdated', {
              detail: {
                plan: state.plan,
                status: state.status,
                isActive: state.isActive
              }
            })
          );

              return subscriptionData;
            }
          }

          throw new Error('Invalid subscription response');
        } catch (error) {
          console.error('Error refreshing subscription:', error);
          setSubscriptionStatus(prev => ({
            ...prev,
            loading: false,
            error: error?.response?.data?.message || error.message || 'Unable to sync subscription'
          }));

          if (!skipRetry && retryAttemptsRef.current < MAX_RETRIES) {
            const delay = Math.min(5000, 1000 * Math.pow(2, retryAttemptsRef.current));
            retryAttemptsRef.current += 1;
            retryTimeoutRef.current = setTimeout(() => {
              refreshSubscription(teamId, {
                force: true,
                skipRetry: retryAttemptsRef.current >= MAX_RETRIES
              });
            }, delay);
          }

          throw error;
        } finally {
          isRefreshingRef.current = false;
          pendingRequestRef.current = null;
        }
      })();

      pendingRequestRef.current = fetchPromise;
      return fetchPromise;
    },
    [buildStateFromSubscription]
  );

  // Function to update subscription status from team data
  const updateSubscriptionFromTeamData = (teamData) => {
    if (!teamData || !teamData.subscription) {
      // If no subscription data, set to default free
      setSubscriptionStatus(prev => ({
        ...prev,
        isActive: false,
        plan: 'free',
        status: 'incomplete',
        loading: false,
        error: null,
        hasCQIntelligence: false,
        limits: {},
        accessiblePages: ['dashboard', 'settings'],
        pageAccess: {}
      }));
      return false;
    }

    try {
      const { plan, status, hasCQIntelligence } = teamData.subscription;
      
      // Normalize plan name to lowercase for consistent comparison
      const planLower = (plan || 'free').toLowerCase();
      
      // Use centralized logic - check if status is active and plan is premium
      const isActive = (status && status.toLowerCase() === 'active') && PREMIUM_PLANS.includes(planLower);
      
      setSubscriptionStatus(prev => ({
        ...prev,
        isActive,
        plan: plan || 'free',
        status: status || 'incomplete',
        loading: false,
        error: null,
        hasCQIntelligence: hasCQIntelligence || false,
        limits: prev.limits || {},
        accessiblePages: prev.accessiblePages || ['dashboard', 'settings'],
        pageAccess: prev.pageAccess || {},
        billingCycle: teamData.subscription.billingCycle || prev.billingCycle || 'monthly',
        currentPeriodStart: teamData.subscription.currentPeriodStart || prev.currentPeriodStart || null,
        currentPeriodEnd: teamData.subscription.currentPeriodEnd || prev.currentPeriodEnd || null,
        stripeCustomerId: teamData.subscription.stripeCustomerId || prev.stripeCustomerId || null,
        stripeSubscriptionId: teamData.subscription.stripeSubscriptionId || prev.stripeSubscriptionId || null
      }));
      
      // Cache the subscription status for faster access on next load
      const subscriptionCache = {
        isActive,
        plan: plan || 'free',
        status: status || 'incomplete',
        hasCQIntelligence: hasCQIntelligence || false,
        limits: {},
        accessiblePages: ['dashboard', 'settings'],
        pageAccess: {},
        billingCycle: teamData.subscription.billingCycle || 'monthly',
        currentPeriodStart: teamData.subscription.currentPeriodStart || null,
        currentPeriodEnd: teamData.subscription.currentPeriodEnd || null,
        stripeCustomerId: teamData.subscription.stripeCustomerId || null,
        stripeSubscriptionId: teamData.subscription.stripeSubscriptionId || null,
        timestamp: Date.now()
      };
      
      // Store in sessionStorage (persists for current session)
      sessionStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
      
      // Also store in localStorage (for session recovery)
      localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
      
      return true;
    } catch (error) {
      console.error('Error updating subscription from team data:', error);
      return false;
    }
  };

  // Listen for team data updated event - but always refresh from API instead of using team data
  useEffect(() => {
    const handleTeamDataUpdated = (e) => {
      if (e.detail && e.detail.teamData && (e.detail.teamData.id || e.detail.teamData._id)) {
        const teamId = e.detail.teamData.id || e.detail.teamData._id;
        refreshSubscription(teamId, { force: true });
      }
    };

    window.addEventListener('teamDataUpdated', handleTeamDataUpdated);
    
    return () => {
      window.removeEventListener('teamDataUpdated', handleTeamDataUpdated);
    };
  }, [refreshSubscription]);

  // Fetch subscription status when team changes - with protection against redundant calls
  useEffect(() => {
    // Determine whether we have cached subscription state for this browser session.
    // Using a ref avoids re-evaluating on every render and lets us refresh silently.
    if (!hasHydratedFromCacheRef.current) {
      try {
        hasHydratedFromCacheRef.current = !!sessionStorage.getItem('subscriptionStatus');
      } catch (e) {
        hasHydratedFromCacheRef.current = false;
      }
    }

    if (teamLoading) {
      return;
    }

    const teamId = selectedTeam?._id;

    if (!teamId) {
      setSubscriptionStatus(prev => ({
        ...prev,
        isActive: false,
        plan: 'free',
        status: 'incomplete',
        loading: false,
        error: null,
        hasCQIntelligence: false,
        limits: {},
        accessiblePages: ['dashboard', 'settings'],
        pageAccess: {},
        billingCycle: 'monthly',
        currentPeriodStart: null,
        currentPeriodEnd: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null
      }));
      hasLoadedRef.current = false;
      currentTeamIdRef.current = null;
      return;
    }

    if (hasLoadedRef.current && currentTeamIdRef.current === teamId && !subscriptionStatus.error) {
      return;
    }

    // If we hydrated from cache, avoid blocking UI with "verifying" loaders.
    // We'll still refresh in the background to keep billing state accurate.
    refreshSubscription(teamId, { silent: hasHydratedFromCacheRef.current === true });
  }, [selectedTeam?._id, teamLoading, refreshSubscription, subscriptionStatus.error]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      pendingRequestRef.current = null;
      isRefreshingRef.current = false;
    };
  }, []);

  // Helper functions
  const canAccessPage = (pageName) => {
    return subscriptionStatus.accessiblePages?.includes(pageName) || false;
  };

  const canPerformAction = (resourceType, currentUsage) => {
    const limit = subscriptionStatus.limits?.[resourceType];
    if (limit === null) return true; // unlimited
    return currentUsage < limit;
  };

  const getRemainingCount = (resourceType, currentUsage) => {
    const limit = subscriptionStatus.limits?.[resourceType];
    if (limit === null) return 'unlimited';
    return Math.max(0, limit - currentUsage);
  };

  return (
    <SubscriptionContext.Provider value={{
      ...subscriptionStatus,
      refreshSubscription,
      canAccessPage,
      canPerformAction,
      getRemainingCount,
      getPageAccess: (pageName) => subscriptionStatus.pageAccess?.[pageName] || {}
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 